import React, { ReactElement } from "react";
import moment from "moment";
import { ListTableColumn } from "../../core";

type LoiColumnKey =
  | "companyName"
  | "createdBy"
  | "createdDateTime"
  | "propertyName"
  | "internalApprovalRequestedBy"
  | "internalApprovalRequestedDateTime"
  | "internalApprovedBy"
  | "internalApprovedDateTime"
  | "clientApprovedBy"
  | "clientApprovedDateTime"
  | "landlordApprovedBy"
  | "landlordApprovedDateTime"
  | "clientSignedBy"
  | "clientSignedDate"
  | "landlordSignedBy"
  | "landlordSignedDate"
  | "clientRejectedBy"
  | "clientRejectedDateTime"
  | "rejectedReason"
  | "version";

const loiColumns: Record<LoiColumnKey, ReactElement> = {
  companyName: (
    <ListTableColumn
      label="Company Name"
      source="leadDetails.company.name"
      column="CompanyName"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  createdBy: (
    <ListTableColumn
      label="Created By"
      source="createdBy.name"
      column="createdBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  createdDateTime: (
    <ListTableColumn
      label="Created Date & Time"
      source="createdAt"
      column="Created Date & Time"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  propertyName: (
    <ListTableColumn
      label="Property Name"
      source="proposedPropertyDetails"
      column="proposedPropertyDetails"
      formatContent={(data) => (
        <div
          style={{
            width: "375px",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {data.map((property: any) => property?.propertyDetails?.propertyName).join(", ")}
        </div>
      )}
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  internalApprovalRequestedBy: (
    <ListTableColumn
      label="Internal Approval Requested By"
      source="document.internalApproval.requestedBy.name"
      column="document.internalApproval.requestedBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  internalApprovalRequestedDateTime: (
    <ListTableColumn
      label="Internal Approval Requested Date & Time"
      source="document.internalApproval.requestedAt"
      column="document.internalApproval.requestedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  internalApprovedBy: (
    <ListTableColumn
      label="Internal Approved By"
      source="document.internalApproval.approvedBy.name"
      column="document.internalApproval.approvedBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  internalApprovedDateTime: (
    <ListTableColumn
      label="Internal Approved Date & Time"
      source="document.internalApproval.approvedAt"
      column="document.internalApproval.approvedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  clientApprovedBy: (
    <ListTableColumn
      label="Client Approved By"
      source="document.clientApproval.approvedBy.name"
      column="document.clientApproval.approvedBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  clientApprovedDateTime: (
    <ListTableColumn
      label="Client Approved Date & Time"
      source="document.clientApproval.approvedAt"
      column="document.clientApproval.approvedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  landlordApprovedBy: (
    <ListTableColumn
      label="Landlord Approved By"
      source="document.landlordApproval.approvedBy.name"
      column="document.landlordApproval.approvedBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  landlordApprovedDateTime: (
    <ListTableColumn
      label="Landlord Approved Date & Time"
      source="document.landlordApproval.approvedAt"
      column="document.landlordApproval.approvedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  clientSignedBy: (
    <ListTableColumn
      label="Client Signed By"
      source="document.clientApproval.signedBy.fullName"
      column="document.clientApproval.signedBy.fullName"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  clientSignedDate: (
    <ListTableColumn
      label="Client Signed Date"
      source="document.clientApproval.signedAt"
      column="document.clientApproval.signedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  landlordSignedBy: (
    <ListTableColumn
      label="Landlord Signed By"
      source="document.landlordApproval.signedBy.fullName"
      column="document.landlordApproval.signedBy.fullName"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  landlordSignedDate: (
    <ListTableColumn
      label="Landlord Signed Date"
      source="document.landlordApproval.signedAt"
      column="document.landlordApproval.signedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  clientRejectedBy: (
    <ListTableColumn
      label="Client Rejected By"
      source="document.internalApproval.rejectedBy.name"
      column="document.internalApproval.rejectedBy.name"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  clientRejectedDateTime: (
    <ListTableColumn
      label="Client Rejected Date & Time"
      source="document.internalApproval.rejectedAt"
      column="document.internalApproval.rejectedAt"
      showDefault
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="loi"
    />
  ),
  rejectedReason: (
    <ListTableColumn
      label="Reason"
      source="document.internalApproval.rejectedReason"
      column="document.internalApproval.rejectedReason"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
  version: (
    <ListTableColumn
      label="Version"
      source="document.versionNumber"
      column="version"
      showDefault
      sortable
      settingsGroup="loi"
    />
  ),
};

const createLoiColumnGroup = (columnKeys: LoiColumnKey[]) =>
  columnKeys.map((key) => loiColumns[key]);

export const DraftColumns = createLoiColumnGroup([
  "companyName",
  "createdBy",
  "createdDateTime",
  "propertyName",
  "version",
]);

export const InternalApprovalColumns = createLoiColumnGroup([
  "companyName",
  "createdBy",
  "createdDateTime",
  "propertyName",
  "internalApprovalRequestedBy",
  "internalApprovalRequestedDateTime",
  "internalApprovedBy",
  "internalApprovedDateTime",
  "version",
]);

export const ClientApprovalColumns = createLoiColumnGroup([
  "companyName",
  "createdBy",
  "createdDateTime",
  "propertyName",
  "clientApprovedBy",
  "clientApprovedDateTime",
  "version",
]);

export const LandlordApprovalColumns = createLoiColumnGroup([
  "companyName",
  "createdBy",
  "createdDateTime",
  "propertyName",
  "landlordApprovedBy",
  "landlordApprovedDateTime",
  "version",
]);

export const SignedColumns = createLoiColumnGroup([
  "companyName",
  "createdBy",
  "createdDateTime",
  "propertyName",
  "clientSignedBy",
  "clientSignedDate",
  "landlordSignedBy",
  "landlordSignedDate",
  "version",
]);

export const RejectedColumns = createLoiColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "clientRejectedBy",
    "clientRejectedDateTime",
    "rejectedReason",
    "version",
  ]);
